import React, { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const checkMatch = (allowed: any, c: any) => {
    const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
    const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const parseAnyDate = (s: any) => {
    if (!s) return null;
    if (typeof s === 'string' && s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    if (typeof s === 'string' && s.includes('.')) {
        const [d, m, y] = s.split('.');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
    return null;
};

const ExpirationView = React.memo(({ customers, currentUser, allUsers, contractStates, onToggleContractState, onSelectCustomer, showToast }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [expTab, setExpTab] = useState<'open' | 'done'>('open');
    const [expDateFrom, setExpDateFrom] = useState('');
    const [expDateTo, setExpDateTo] = useState('');
    const [expSparteFilter, setExpSparteFilter] = useState('');

    const myCustomers = useMemo(() => {
        if (!currentUser) return [];
        if (currentUser.role === 'admin') return customers;
        const allowedUserIds = [currentUser.id, ...(currentUser.allowedToView || [])];
        return customers.filter((c: any) => checkMatch(allUsers.filter((u: any) => allowedUserIds.includes(u.id)), c));
    }, [customers, currentUser, allUsers]);

    // Helper für Tab-Filter-Berechnung
    const getFilteredExpirations = (filterTab: 'open' | 'done' | null) => {
        let results: any = [];
        if (!currentUser) return [];
        
        myCustomers.forEach((c: any) => {
            (c.verträge || []).forEach((v: any) => {
                const safeId = (v.vsnr || '').replace(/\//g, '_');
                const isDone = contractStates[safeId] === 'done';
                const matchesTab = filterTab === null || (filterTab === 'open' && !isDone) || (filterTab === 'done' && isDone);
                
                // Sparten-Filter
                if (expSparteFilter && v.sparte !== expSparteFilter) return;
                
                // Datums-Filter (Von-Bis)
                let matchesDateFilter = true;
                const expiryDate = parseAnyDate(v.ablauf);
                if (!expiryDate) matchesDateFilter = false;
                else if (expDateFrom || expDateTo) {
                    const from = expDateFrom ? new Date(expDateFrom) : new Date(0);
                    const to = expDateTo ? new Date(expDateTo) : new Date(9999, 11, 31);
                    matchesDateFilter = expiryDate >= from && expiryDate <= to;
                } else {
                    // Standardansicht: Verträge vom 01.01.2000 bis in die nächsten 5 Monate
                    const defaultFrom = new Date(2000, 0, 1);
                    const defaultTo = new Date();
                    defaultTo.setMonth(defaultTo.getMonth() + 5);
                    matchesDateFilter = expiryDate >= defaultFrom && expiryDate <= defaultTo;
                }
                
                const sparteLower = (v.sparte || '').toLowerCase().trim();
                const isKfzOrLeben = /kfz|kraftfahrzeug|lebensversicherung|^leben$/.test(sparteLower) || sparteLower === 'lv';
                if (matchesTab && matchesDateFilter && !isKfzOrLeben) {
                    results.push({ customer: c, contract: v, id: safeId, status: isDone ? 'done' : 'open' });
                }
            });
        });
        
        return results.sort((a: any, b: any) => (parseAnyDate(a.contract.ablauf)?.getTime() || 0) - (parseAnyDate(b.contract.ablauf)?.getTime() || 0));
    };

    // Zähler für "Offen"
    const openCount = useMemo(() => {
        return getFilteredExpirations('open').length;
    }, [myCustomers, contractStates, expDateFrom, expDateTo, expSparteFilter, currentUser]);

    // Zähler für "Erledigt"
    const doneCount = useMemo(() => {
        return getFilteredExpirations('done').length;
    }, [myCustomers, contractStates, expDateFrom, expDateTo, expSparteFilter, currentUser]);

    const displayedExpirations = useMemo(() => {
        return getFilteredExpirations(expTab);
    }, [myCustomers, contractStates, expTab, expDateFrom, expDateTo, expSparteFilter, currentUser]);

    const getDaysUntil = (dateStr: string) => {
        const date = parseAnyDate(dateStr);
        if (!date) return 999;
        const today = new Date();
        const diff = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const getUrgencyColor = (daysUntil: number) => {
        if (daysUntil <= 7) return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100' };
        if (daysUntil <= 30) return { bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100' };
        return { bg: 'bg-yellow-50', text: 'text-yellow-700', badge: 'bg-yellow-100' };
    };

    return (
        <PageLayout>
            <PageHeader
                title="Abläufe"
                subtitle={
                    <span className="flex items-center gap-2 mt-1">
                        <Calendar size={16} />
                        Verträge vom 01.01.2000 bis in die nächsten 5 Monate
                    </span>
                }
            />

            <PageCard className="p-6 space-y-4">
                <div className="flex gap-3 flex-wrap">
                    <button 
                        onClick={() => setExpTab('open')} 
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all border ${
                            expTab === 'open' 
                                ? (isDark ? 'bg-red-500/50 border-red-400/80 text-white shadow-lg shadow-red-500/20' : 'bg-red-500 border-red-600 text-white shadow-lg shadow-red-500/20')
                                : (isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100')
                        }`}
                    >
                        📋 Offen ({openCount})
                    </button>
                    <button 
                        onClick={() => setExpTab('done')} 
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all border ${
                            expTab === 'done' 
                                ? (isDark ? 'bg-green-500/50 border-green-400/80 text-white shadow-lg shadow-green-500/20' : 'bg-green-500 border-green-600 text-white shadow-lg shadow-green-500/20')
                                : (isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100')
                        }`}
                    >
                        ✓ Erledigt ({doneCount})
                    </button>
                </div>

                <div className={`border-t ${isDark ? 'border-white/20' : 'border-slate-200'} pt-4 space-y-3`}>
                    <p className={`text-xs font-bold ${isDark ? 'text-white/80' : 'text-slate-700'} uppercase`}>Filter</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Von Datum:</label>
                            <input 
                                type="date" 
                                value={expDateFrom} 
                                onChange={(e) => setExpDateFrom(e.target.value)} 
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Bis Datum:</label>
                            <input 
                                type="date" 
                                value={expDateTo} 
                                onChange={(e) => setExpDateTo(e.target.value)} 
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'} block`}>Sparte:</label>
                            <select 
                                value={expSparteFilter} 
                                onChange={(e) => setExpSparteFilter(e.target.value)} 
                                className={`w-full px-3 py-2 rounded-xl text-sm transition-all ${isDark ? 'glass-input bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'}`}
                            >
                                <option value="">— Alle Sparten —</option>
                                {Array.from(new Set(myCustomers.flatMap((c: any) => (c.verträge || []).map((v: any) => v.sparte)))).sort().filter((s: any) => s && !s.toLowerCase().match(/leben|kfz/)).map((sparte: any) => (
                                    <option key={sparte} value={sparte}>{sparte}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button 
                        onClick={() => { setExpDateFrom(''); setExpDateTo(''); setExpSparteFilter(''); }} 
                        className={`text-xs font-bold transition px-3 py-1.5 rounded-full ${isDark ? 'text-white/60 hover:text-white/80 hover:bg-white/10' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                    >
                        ↻ Alle Filter zurücksetzen
                    </button>
                </div>
                <p className={`text-xs pt-3 border-t ${isDark ? 'border-white/20 text-white/50' : 'border-slate-200 text-slate-500'}`}>
                    Verlängerungs-Links senden und Bestätigungen: Menü <strong>Verlängerung</strong> (nur Kunden mit E-Mail).
                </p>
            </PageCard>

            <PageCard>
                {displayedExpirations.length === 0 ? (
                    <div className={pageStyles.empty(isDark)}>
                        <AlertCircle size={48} className={`mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-slate-300'}`} />
                        <p className="font-medium">Keine Verträge in dieser Ansicht</p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Passe die Filter an oder prüfe die Kundenstammdaten in der Kundenübersicht.</p>
                    </div>
                ) : (
                    <div className={`divide-y ${isDark ? 'divide-white/10' : 'divide-slate-100'}`}>
                        {displayedExpirations.map((item: any, idx: any) => {
                            const daysUntil = getDaysUntil(item.contract.ablauf);
                            const urgency = getUrgencyColor(daysUntil);
                            const isOverdue = daysUntil < 0;

                            return (
                                <div 
                                    key={idx} 
                                    className={`p-4 transition-all border-l-4 cursor-pointer ${
                                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                                    } ${
                                        item.status === 'done' ? '' : (
                                            isOverdue ? (isDark ? 'border-l-red-600 bg-red-500/10' : 'border-l-red-600 bg-red-50') :
                                            daysUntil <= 7 ? 'border-l-red-500' :
                                            daysUntil <= 30 ? 'border-l-orange-500' :
                                            'border-l-yellow-500'
                                        )
                                    }`}
                                    onClick={() => onSelectCustomer?.(item.customer)}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <p className={`font-bold text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.customer.vorname} {item.customer.name}</p>
                                                <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${isDark ? 'text-white/80 bg-white/10 border-white/20' : 'text-slate-700 bg-slate-100 border-slate-200'}`}>
                                                    {item.customer.zustMA}
                                                </span>
                                            </div>
                                            <div className="flex gap-3 flex-wrap items-center text-sm">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${item.status === 'done' ? (isDark ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500') : (urgency.badge + ' ' + urgency.text)}`}>
                                                    {item.contract.sparte}
                                                </span>
                                                <span className={`font-mono text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.contract.vsnr}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                                            <div className="text-right">
                                                <p className={`font-mono font-bold text-sm ${item.status === 'done' ? (isDark ? 'text-white/60' : 'text-slate-500') : urgency.text}`}>
                                                    {item.contract.ablauf}
                                                </p>
                                                <p className={`text-xs font-semibold ${item.status === 'done' ? (isDark ? 'text-white/60' : 'text-slate-500') : urgency.text}`}>
                                                    {item.status === 'open' && (isOverdue ? `⚠️ Überfällig ${Math.abs(daysUntil)}d` : `${daysUntil} Tage`)}
                                                </p>
                                            </div>
                                            <button 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    const nextStatus = item.status === 'done' ? 'open' : 'done';
                                                    onToggleContractState(item.id, nextStatus); 
                                                }}
                                                className={`h-10 w-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 font-bold border ${
                                                    expTab === 'open' 
                                                        ? (isDark ? 'bg-white/10 border-white/20 text-white/60 hover:bg-green-500/50 hover:border-green-400/80 hover:text-white' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-green-500 hover:border-green-600 hover:text-white')
                                                        : (isDark ? 'bg-green-500/50 border-green-400/80 text-white hover:bg-white/10 hover:border-white/20 hover:text-white/60' : 'bg-green-500 border-green-600 text-white hover:bg-slate-100 hover:border-slate-200 hover:text-slate-600')
                                                }`}
                                                title={expTab === 'open' ? 'Als erledigt markieren' : 'Zurück auf offen'}
                                            >
                                                {expTab === 'open' ? <CheckCircle size={18} /> : <RefreshCw size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </PageCard>

        </PageLayout>
    );
});

ExpirationView.displayName = 'ExpirationView';
export default ExpirationView;
